import { clsx } from 'clsx';
import { useDockPosition } from '../../hooks/useMediaQuery';
import { DOCK_TABS, DOCK_TAB_INFO, type DockTab } from '../../stores/characterCreationStore';
import { CheckIcon } from '../ui/Icons';

export type ValidationStatus = 'valid' | 'invalid' | 'incomplete';

interface DockNavigationProps {
  currentTab: DockTab;
  onTabChange: (tab: DockTab) => void;
  validationStatus: Record<DockTab, ValidationStatus>;
}

export function DockNavigation({ currentTab, onTabChange, validationStatus }: DockNavigationProps) {
  const position = useDockPosition();

  return (
    <nav
      className={clsx(
        'fixed z-40 flex bg-background-card/80 backdrop-blur-lg border-border-default',
        position === 'bottom'
          ? 'bottom-0 left-0 right-0 flex-row justify-center gap-6 px-4 py-3 border-t safe-area-bottom'
          : 'top-0 left-0 bottom-0 flex-col justify-center gap-3 px-3 py-4 border-r w-24'
      )}
    >
      {DOCK_TABS.map((tab) => (
        <DockIcon
          key={tab}
          tab={tab}
          isActive={currentTab === tab}
          status={validationStatus[tab]}
          onClick={() => onTabChange(tab)}
          position={position}
        />
      ))}
    </nav>
  );
}

interface DockIconProps {
  tab: DockTab;
  isActive: boolean;
  status: ValidationStatus;
  onClick: () => void;
  position: 'bottom' | 'left';
}

function DockIcon({ tab, isActive, status, onClick, position }: DockIconProps) {
  const info = DOCK_TAB_INFO[tab];

  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'relative flex flex-col items-center justify-center transition-all duration-200',
        position === 'bottom' ? 'w-16 h-16' : 'w-16 h-20',
        isActive
          ? 'scale-110'
          : 'hover:scale-105 active:scale-95'
      )}
      aria-label={info.label}
      aria-current={isActive ? 'page' : undefined}
    >
      {/* Icon container */}
      <div
        className={clsx(
          'flex items-center justify-center w-14 h-14 rounded-xl transition-all duration-200',
          isActive
            ? 'bg-accent-gold text-background-primary shadow-lg shadow-accent-gold/30'
            : 'bg-background-elevated text-text-secondary hover:bg-background-secondary hover:text-text-primary'
        )}
      >
        <TabIcon icon={info.icon} className="w-7 h-7" />
      </div>

      {/* Label */}
      <span
        className={clsx(
          'text-xs mt-1 font-semibold uppercase tracking-wider transition-colors',
          isActive ? 'text-accent-gold' : 'text-text-secondary'
        )}
      >
        {info.label}
      </span>

      {/* Status indicator */}
      {!isActive && status === 'valid' && (
        <span className="absolute -top-0.5 -right-0.5 text-success">
          <CheckIcon className="w-3 h-3" />
        </span>
      )}
      {!isActive && status !== 'valid' && (
        <span className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-danger" />
      )}
    </button>
  );
}

function TabIcon({ icon, className }: { icon: string; className?: string }) {
  switch (icon) {
    case 'user':
      return (
        <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      );
    case 'backpack':
      return (
        <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
        </svg>
      );
    case 'wand':
      return (
        <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          {/* Magic wand with sparkle */}
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 4l-11 11 4 4 11-11-4-4z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M18 2l1 2 2 1-2 1-1 2-1-2-2-1 2-1 1-2z" />
          <circle cx="19" cy="12" r="1" fill="currentColor" />
          <circle cx="12" cy="5" r="0.5" fill="currentColor" />
        </svg>
      );
    default:
      return null;
  }
}
